/**
 * Pure geometry for the Zone map (`ZoneMap.tsx`): lat/lon → SVG user units.
 *
 * Equirectangular: longitude is scaled by cos(mean latitude) so a kilometre
 * east and a kilometre north are the same length on screen (at 45.8° N a
 * degree of longitude is ~0.70 of a degree of latitude). The frame's lat/lon
 * box is cut at exactly the viewBox aspect, so that scaling is already in
 * the box and the mapping itself is linear — plain + − × ÷, which every JS
 * engine computes identically.
 *
 * No DOM, no randomness, no locale: the server and the first client render
 * compute the same coordinates, so the SVG hydrates byte-identical.
 */

import frame from '../../data/mapFrame.json'

/**
 * The relief map's fixed frame (`data/mapFrame.json`): the extent the raster
 * was rendered for and the viewBox it is drawn in (a kilometre east and a
 * kilometre north are the same length, see the test).
 * `scripts/media/render-relief.mjs` and `fetch-map-features.mjs` read the
 * same JSON and use the same formula — change the frame there and here
 * together, then re-run both scripts.
 */
export interface MapFrame {
  minLat: number
  maxLat: number
  minLon: number
  maxLon: number
  width: number
  height: number
}

export const FRAME: MapFrame = frame

/** `p` in the frame's viewBox, unrounded (y grows downwards: north is up). */
export function frameProject(p: { lat: number; lon: number }, f: MapFrame = FRAME): { x: number; y: number } {
  return {
    x: ((p.lon - f.minLon) / (f.maxLon - f.minLon)) * f.width,
    y: ((f.maxLat - p.lat) / (f.maxLat - f.minLat)) * f.height,
  }
}

/** Kilometres per user unit (one degree of latitude ≈ 111.195 km). */
export function frameKmPerUnit(f: MapFrame = FRAME): number {
  return ((f.maxLat - f.minLat) * 111.195) / f.height
}

/**
 * Two decimals, no trailing zeros (`3.456` → `3.46`, `1` → `1`). Every
 * coordinate that reaches the markup goes through it (or `compactPath`),
 * so the prerendered attributes are short and identical to the client's.
 */
export function round2(n: number): number {
  const r = Math.round(n * 100) / 100
  return r === 0 ? 0 : r // no "-0"
}

/** An open polyline through `points` in order: `'M x y L x y …'` (empty string for no points). */
export function valleyPath(points: readonly { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${round2(p.x)} ${round2(p.y)}`).join(' ')
}

/** One decimal, no trailing zeros, no "-0". */
function round1(n: number): number {
  const r = Math.round(n * 10) / 10
  return r === 0 ? 0 : r
}

/**
 * Dense geometry (rivers, roads, shorelines — thousands of vertices) as the
 * shortest path data: one decimal (0.1 unit ≈ 5 m, far below a pixel) and
 * implicit line-tos (`M1 2L3 4 5 6`), optionally closed.
 */
export function compactPath(points: readonly { x: number; y: number }[], closed = false): string {
  if (points.length === 0) return ''
  const [first, ...rest] = points
  const head = `M${round1(first.x)} ${round1(first.y)}`
  const body = rest.length ? `L${rest.map((p) => `${round1(p.x)} ${round1(p.y)}`).join(' ')}` : ''
  return `${head}${body}${closed ? 'Z' : ''}`
}

/**
 * The whole-pixel `scrollLeft` that centres user-unit `x` of a drawing
 * `units` wide, rendered `scrollWidth` px wide in a `clientWidth` px
 * scroller — clamped to `[0, scrollWidth − clientWidth]`, and 0 when nothing
 * overflows. Assumes the drawing fills the scroll width (px per unit =
 * `scrollWidth / units`).
 */
export function centredScrollLeft(x: number, units: number, scrollWidth: number, clientWidth: number): number {
  const max = scrollWidth - clientWidth
  if (max <= 0) return 0
  return Math.round(Math.min(max, Math.max(0, (x / units) * scrollWidth - clientWidth / 2)))
}
